/**
 * 语音转文字（ASR）链路：上传录音 -> 触发识别任务 -> 轮询识别结果。
 *
 * 用的是 english-user-management 后台**已有的同一套接口**（不新增后端能力）：
 *   - POST /api/base/upload    附件上传（FormData: file），返回附件 id
 *   - POST /api/base/asr       触发腾讯云语音识别任务，返回 taskId
 *                              ⚠️ 该接口返回体是 { success, data: { data: { taskId } } }，
 *                              比 asrResult **多一层 data**（与后台取数口径一致）
 *   - POST /api/base/asrResult 查询识别结果，data.status === 2 表示识别完成，
 *                              data.result 为识别文本
 * 与后台 components/StudentTask/index.vue 的 upload -> asr -> asrResult 轮询流程一致
 * （那边直接调 @/api/base 的 upload / asr / asrResult）。
 *
 * 上传参数沿用后台 upload() 的固定值：uploadType=4（答题录音）、type=3。
 */
import req from "./req";

/**上传录音文件，resolve 后端响应体（{ success, data: { id } }）**/
export function uploadVoiceBlob(blob, fileName) {
  const fd = new FormData();
  fd.append("file", blob, fileName || `voice_input_${Date.now()}.mp3`);
  return req
    .post("/api/base/upload?businessId=1&uploadType=4&type=3", fd)
    .then(res => res.data);
}

/**触发语音识别任务，resolve 后端响应体**/
export function createAsrTask(attachmentId) {
  return req.post("/api/base/asr", { attachmentId }).then(res => res.data);
}

/**查询语音识别结果，resolve 后端响应体（{ success, data: { status, result } }）**/
export function queryAsrResult(taskId) {
  return req.post("/api/base/asrResult", { taskId }).then(res => res.data);
}

/**
 * 归一化 /api/base/asr* 响应体的 data 层。
 *
 * 注意：/api/base/asr（创建任务）的返回体比 /api/base/asrResult **多套一层 data**
 * ——后台 components/StudentTask/index.vue 就是按 `asrRes.data.data.taskId` 取的
 * （后台 request 拦截器直接 resolve response.data，而我们 req 拿到的是 axios
 * 原始 response，故这里对 body 做统一剥壳）。为稳妥同时兼容单层结构。
 */
function unwrapAsrData(body) {
  if (!body || !body.data) return null;
  const d = body.data;
  return d.data && typeof d.data === "object" ? d.data : d;
}

/**取识别任务 id**/
function pickAsrTaskId(body) {
  const d = unwrapAsrData(body);
  return d ? d.taskId || d.taskID || "" : "";
}

/**取识别文本**/
function pickAsrText(body) {
  const d = unwrapAsrData(body);
  return d ? d.result || "" : "";
}

/**取识别状态（2 === 识别完成）**/
function pickAsrStatus(body) {
  const d = unwrapAsrData(body);
  return d ? d.status : undefined;
}

/**
 * 录音 blob -> 文本（完整链路）。
 * @param {Blob} blob 录音文件（mp3）
 * @param {Object} options { maxTimes 轮询次数上限，interval 轮询间隔 ms }
 * @returns {Promise<string>} 识别文本（可能为空串）
 */
export async function voiceToText(blob, options = {}) {
  const { maxTimes = 15, interval = 1200 } = options;

  // 1) 上传录音文件
  const upRes = await uploadVoiceBlob(blob);
  if (!upRes || !upRes.success || !upRes.data || !upRes.data.id) {
    throw new Error("录音上传失败");
  }
  const attachmentId = upRes.data.id;

  // 2) 触发 ASR 任务
  const asrRes = await createAsrTask(attachmentId);
  const taskId = pickAsrTaskId(asrRes);
  if (!asrRes || !asrRes.success || !taskId) {
    // 打印原始响应，便于联调时定位（真实错误码/结构一目了然）
    console.warn("[asr] 创建识别任务返回异常:", asrRes);
    throw new Error("ASR任务创建失败");
  }

  // 3) 轮询识别结果（status === 2 为识别完成）
  let last = null;
  for (let i = 0; i < maxTimes; i++) {
    try {
      const res = await queryAsrResult(taskId);
      last = res;
      if (res && res.success && Number(pickAsrStatus(res)) === 2) break;
    } catch (e) {
      // 忽略单次查询错误，继续轮询
    }
    if (i < maxTimes - 1) {
      await new Promise(r => setTimeout(r, interval));
    }
  }
  const text = last && last.success ? pickAsrText(last) : "";
  return String(text).trim();
}

export default {
  uploadVoiceBlob,
  createAsrTask,
  queryAsrResult,
  voiceToText,
};
